/**
 * FormCard — reusable single-page form shell.
 *
 * Provides a consistent card wrapper with title, icon, subtitle,
 * content area, and footer with Cancel/Submit buttons.
 * Shows FormSkeleton when isLoading is true.
 *
 * Usage:
 *   <FormCard title="Créer un collaborateur" onSubmit={handleSubmit} isPending={isPending}>
 *     <FormField label="Nom">
 *       <input {...register("nom")} className="input input-bordered w-full" />
 *     </FormField>
 *   </FormCard>
 */

import { FormSkeleton } from "@components/common/skeletons/FormSkeleton";
import { cn } from "@lib/utils/cn";
import type { LucideIcon } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";

export interface FormCardProps {
  /** Page title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Lucide icon displayed with title */
  icon?: LucideIcon;
  /** Form content */
  children: ReactNode;
  /** Submit handler */
  onSubmit: () => void;
  /** Is form submitting */
  isPending?: boolean;
  /** Cancel handler (hides button if not provided) */
  onCancel?: () => void;
  /** Submit button text */
  submitText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Show loading skeleton instead of children */
  isLoading?: boolean;
  /** Additional classes for the card */
  className?: string;
}

export function FormCard({
  title,
  subtitle,
  icon: Icon,
  children,
  onSubmit,
  isPending = false,
  onCancel,
  submitText = "Enregistrer",
  cancelText = "Annuler",
  isLoading = false,
  className,
}: FormCardProps) {
  if (isLoading) {
    return (
      <div className="card bg-base-100 shadow-lg shadow-primary/5 border border-base-300">
        <div className="card-body">
          <div className="skeleton h-5 w-3/4 mb-4 rounded" />
          <FormSkeleton fields={4} hasSubmitButton className="" />
        </div>
      </div>
    );
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !isPending) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div
      className={cn(
        "card bg-base-100 shadow-lg shadow-primary/5 border border-base-300",
        className,
      )}
      onKeyDown={handleKeyDown}
    >
      <div className="card-body gap-4">
        {/* Header */}
        <div>
          <h2 className="card-title text-lg flex items-center gap-2">
            {Icon && <Icon className="w-5 h-5" />}
            {title}
          </h2>
          {subtitle && <p className="text-base-content/60 text-sm">{subtitle}</p>}
        </div>

        <div className="border-t border-base-300/50" />

        {/* Content */}
        <div>{children}</div>

        <div className="border-t border-base-300/50" />

        {/* Footer */}
        <div className="card-actions justify-end">
          {onCancel && (
            <button
              type="button"
              className="btn btn-ghost transition-all duration-200 active:scale-95 focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none"
              onClick={onCancel}
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary shadow-sm shadow-primary/20 hover:shadow-md hover:shadow-primary/30 transition-all duration-200 active:scale-95"
            onClick={onSubmit}
            disabled={isPending}
          >
            {isPending && <span className="loading loading-spinner loading-sm" />}
            {submitText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FormCard;
